import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { Observable, catchError, finalize, shareReplay, switchMap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth/auth.service';
import { ToastrService } from '../../services/toastr/toastr.service';
import { environment } from 'src/environments/environment';

/**
 * Mantém um único refresh de token em andamento (single-flight), compartilhado
 * entre todas as requisições que receberem 401 simultaneamente.
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshCoordinator {
  private readonly authService = inject(AuthService);

  private refreshRequest$?: Observable<string>;

  getRefreshRequest(): Observable<string> {
    // refreshAccessToken() já persiste e emite o NOVO token; usar o valor
    // emitido (e não reler o localStorage) evita repetir o token expirado.
    this.refreshRequest$ ??= this.authService.refreshAccessToken().pipe(
      finalize(() => {
        this.refreshRequest$ = undefined;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.refreshRequest$;
  }
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // Nunca enviar credenciais da sessão para serviços externos (CEP, CNPJ, SDKs etc.).
  if (!isBackendRequest(req.url)) {
    return next(req);
  }

  // Pular requisições do próprio backend que não precisam de autenticação.
  if (
    req.url.includes('format=jsonp') ||
    req.url.includes('/auth/login') ||
    req.url.includes('/auth/refresh-token')
  ) {
    return next(req);
  }

  const authService = inject(AuthService);
  const token = authService.getToken();

  // Se não há token, permitir a requisição (o backend decidirá se autoriza)
  if (!token) {
    return next(req);
  }

  const refreshCoordinator = inject(TokenRefreshCoordinator);
  const router = inject(Router);
  const toast = inject(ToastrService);

  const tokenizedReq = req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`
    }
  });

  return next(tokenizedReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        return handleTokenExpired(req, next, refreshCoordinator, authService, router, toast);
      }
      return throwError(() => error);
    })
  );
};

function isBackendRequest(requestUrl: string): boolean {
  try {
    const backendUrl = new URL(environment.BaseURL);
    const resolvedRequestUrl = new URL(requestUrl, backendUrl);
    const backendPath = backendUrl.pathname.replace(/\/$/, '');

    return resolvedRequestUrl.origin === backendUrl.origin
      && (resolvedRequestUrl.pathname === backendPath
        || resolvedRequestUrl.pathname.startsWith(`${backendPath}/`));
  } catch {
    // Configuração inválida não deve resultar no envio de credenciais por engano.
    return false;
  }
}

function handleTokenExpired(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  refreshCoordinator: TokenRefreshCoordinator,
  authService: AuthService,
  router: Router,
  toast: ToastrService
): Observable<HttpEvent<unknown>> {
  return refreshCoordinator.getRefreshRequest().pipe(
    switchMap((newToken) => {
      // Refazer a requisição original com o novo token
      const newRequest = request.clone({
        setHeaders: {
          Authorization: `Bearer ${newToken}`
        }
      });
      return next(newRequest);
    }),
    catchError((error) => {
      // Várias requisições podem falhar juntas; só a primeira faz o logout/aviso.
      if (authService.isLogged()) {
        authService.removeToken();
        router.navigate(['/auth/login']);
        toast.error('Sua sessão expirou. Por favor faça login novamente.');
      }
      return throwError(() => error);
    })
  );
}
